import { reaction } from 'mobx';
import React from 'react';
import ReactDom from 'react-dom';
import { SlideInterface } from '@tencent/docs-api';
import { DrawerService, InjectorService } from '@tencent/workbench';
import { DrawerName } from '@src/framework/workbench/drawer/constants';
import { IMachineAuditor, IMachineAuditDelegateService } from './machine-auditor.interface';
import { AnalyzerType, MachineAuditChecker } from './machine-audit-checker/machine-audit-cheker';
import { MachineContentLayoutAnalyzer } from './machine-audit-analyzer/machine-content-layout-analyzer';
import { MachineRepeatAnalyzer } from './machine-audit-analyzer/machine-repeat-analyser';
import { MachineFontAnalyzer } from './machine-audit-analyzer/machine-font-analyzer';
import { MachineImageAnalyzer } from './machine-audit-analyzer/machine-image-analyzer';
import { MachineMasterAnalyzer } from './machine-audit-analyzer/machine-master-analyzer';
import { MachineOutsideAnalyzer } from './machine-audit-analyzer/machine-outside-analyzer';
import { MachineOverlapAnalyzer } from './machine-audit-analyzer/machine-overlap-analyzer';
import { MachineReviewAnalyzer } from './machine-audit-analyzer/machine-review-analyzer';
import { MachineContentTypeAnalyzer } from './machine-audit-analyzer/machine-content-type-analyzer';
import { MachineChartAnalyzer } from './machine-audit-analyzer/machine-chart-analyzer';
import { IDrawerStateManager } from '../drawer-state-manager/core/drawer-state-manager.interface';
import { showTemplateMachineAudit } from '@src/js/common/ark-extensions/dc-components/template-machine-audit';
import SlideApp from '@src/framework/docs-api/slide-app';
import SlideFeature from '@src/framework/docs-api/slide-feature';
import { EDIT_PANEL_ID } from '@src/framework/editor/src/page/index/components/canvas/canvas-constants';
import { MachineAuditBorder } from './machine-audit-border/machine-audit-border';
import { Observer } from 'mobx-react';
import { MachineColorAnalyzer } from './machine-audit-analyzer/machine-color-analyzer';

/**
 * slide 模版审核模块
 * 对第三方的模版进行合规性、美观度、适用度的审核
 * 实现逻辑复用排版检查的各种 analyzer 模块，整体功能类似于 SlideContentLayoutAnalyzer
 */
export class SlideMachineAuditor implements IMachineAuditor {
    private drawerService: DrawerService;
    private drawerStateManager?: IDrawerStateManager;
    private services: IMachineAuditDelegateService;

    private slideTemplateChecker: MachineAuditChecker;
    private checkerInitialing: Promise<void>; // 初始化 promise
    private checkBorderElement: HTMLDivElement;

    public setDelegateService(services: IMachineAuditDelegateService) {
        this.services = services;
        this.drawerService = InjectorService.get(DrawerService);
        this.checkerInitialing = this.init(); // 初始化 checker
        reaction(
            () => this.services.rightSideBarManager.isMachineAuditShown,
            (show) => {
                if (show) {
                    this.showCheckDrawer();
                } else {
                    this.drawerStateManager?.handleDrawerHolderVisiable(false);
                }
                window.dispatchEvent(new Event('resize'));
            },
        );
    }

    public showDrawer(): void {
        this.services.rightSideBarManager.setMachineAuditShown(true);
    }

    public closeDrawer(): void {
        this.drawerStateManager?.closeRightDrawer(DrawerName.MACHINE_AUDIT);
        this.services.rightSideBarManager.setMachineAuditShown(false);
    }

    public async analysis(): Promise<SlideInterface.ITemplateCheckData> {
        await this.checkerInitialing;
        return this.slideTemplateChecker.analysis();
    }

    public showBorder(options: SlideInterface.MachineAuditHighlightProps) {
        if (!this.checkBorderElement) {
            const editPanel = document.getElementById(EDIT_PANEL_ID);
            this.checkBorderElement = document.createElement('div');
            editPanel?.appendChild(this.checkBorderElement);
        }
        ReactDom.render(
            <Observer>
                {() => {
                    const slide = this.services.selectionService.currentSlide;
                    const childrenConverted = slide?.childrenConverted;
                    return slide && childrenConverted ? <MachineAuditBorder slide={slide} options={options} /> : <></>;
                }}
            </Observer>,
            this.checkBorderElement,
        );
    }

    private async showCheckDrawer() {
        this.drawerStateManager = await this.services.drawerStateManager.promise();
        this.drawerService.showRightDrawer({
            name: DrawerName.MACHINE_AUDIT,
            content: <div id="template-check-drawer" />,
            style: { display: 'flex', flexDirection: 'column' },
            onClose: () => this.services.rightSideBarManager.setMachineAuditShown(false),
            onOpen: () =>
                showTemplateMachineAudit({
                    onClose: () => this.closeDrawer(),
                    slideApp: SlideApp.getInstance(),
                    slideFeature: SlideFeature.getInstance(),
                    container: document.getElementById('template-check-drawer') ?? undefined,
                }),
        });
        this.drawerStateManager?.handleDrawerHolderVisiable(true);
    }

    private async init() {
        const { enumerator, uiDataService } = this.services;
        this.slideTemplateChecker = new MachineAuditChecker(enumerator);

        // 在这里可以自由添加解析器（Analyzer）
        const contentLayoutAnalyzer = new MachineContentLayoutAnalyzer(uiDataService);
        const imageAnalyzer = new MachineImageAnalyzer();
        const chartAnalyzer = new MachineChartAnalyzer();
        const reviewAnalyzer = new MachineReviewAnalyzer(contentLayoutAnalyzer, imageAnalyzer, chartAnalyzer);
        const overlapAnalyzer = new MachineOverlapAnalyzer(uiDataService);
        const colorAnalyzer = new MachineColorAnalyzer(uiDataService);
        const outsideAnalyzer = new MachineOutsideAnalyzer(uiDataService);
        const fontAnalyzer = new MachineFontAnalyzer(reviewAnalyzer);
        const masterAnalyzer = new MachineMasterAnalyzer();
        const contentTypeAnalyzer = new MachineContentTypeAnalyzer(reviewAnalyzer);
        const repeatAnalyzer = new MachineRepeatAnalyzer(reviewAnalyzer);
        const rootIds = SlideInterface.CheckResultItemRootIds;
        this.slideTemplateChecker.addAnalyzers([
            { type: AnalyzerType.Other, analyzer: contentLayoutAnalyzer },
            { type: AnalyzerType.Other, analyzer: imageAnalyzer },
            { type: AnalyzerType.Other, analyzer: chartAnalyzer },
            { type: AnalyzerType.Review, analyzer: reviewAnalyzer },
            { type: AnalyzerType.Common, analyzer: overlapAnalyzer, id: rootIds.ELEMENT },
            { type: AnalyzerType.Common, analyzer: colorAnalyzer, id: rootIds.ELEMENT },
            { type: AnalyzerType.Common, analyzer: outsideAnalyzer, id: rootIds.ELEMENT },
            { type: AnalyzerType.Common, analyzer: fontAnalyzer, id: rootIds.COPYRIGHT },
            { type: AnalyzerType.Common, analyzer: masterAnalyzer, id: rootIds.SLIDE_MASTER_LAYOUT },
            { type: AnalyzerType.Common, analyzer: repeatAnalyzer, id: rootIds.ELEMENT },
            { type: AnalyzerType.Common, analyzer: contentTypeAnalyzer, id: rootIds.SLIDE_AI },
        ]);
    }
}
